import { notifyError } from "@/lib/notify-error";
import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { ChevronDown, ChevronRight, FileText, GitBranchPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { useProject } from "@/components/projects";
import { JobListPage, jobListDateColumn, type JobListColumn } from "@/components/layout";
import { RowAction, RowActionsMenu } from "@/components/row-actions-menu";
import { RevisionBadge } from "@/components/semantic-badges";
import type { StatusKey } from "@/components/status-badge";
import { StatusDot } from "@/components/status-dot";
import { IconButton } from "@/components/ui/icon-button";
import { InlineEditNameCell } from "@/components/inline-edit-name-cell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { CreateTakeoffRevisionDialog } from "@/features/takeoff-pilot/create-takeoff-revision-dialog";
import {
  getTakeoffRevisionNumber,
  groupTakeoffJobsByRevision,
} from "@/lib/takeoff-revisions";
import { toTitleCase } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/takeoffPilot/")({
  component: TakeoffPilotPage,
});

const USER_STATUS_OPTIONS = [
  { value: "initiated", label: "Initiated" },
  { value: "ready_for_review", label: "Ready For Review" },
  { value: "done", label: "Done" },
] as const;

type TakeoffUserStatus = (typeof USER_STATUS_OPTIONS)[number]["value"];

const DEFAULT_USER_STATUS: TakeoffUserStatus = "initiated";

const TAKEOFF_STATUS_LABELS: Record<string, string> = {
  initiated: "Initiated",
  ready_for_review: "Ready For Review",
  done: "Done",
  copying_revision: "Copying Revision",
  extracting_schedule: "Extracting Schedule",
  ready_for_tag_selection: "Ready For Tag Selection",
  indexing_finish_plan: "Indexing Finish Plan",
  resolving_tag_counts: "Resolving Tag Counts",
  ready_for_results: "Ready For Results",
  completed: "Completed",
  failed: "Failed",
};

function getTakeoffStatusLabel(status: string): string {
  return TAKEOFF_STATUS_LABELS[status] ?? toTitleCase(status);
}

/** Maps TakeoffPilot user/pipeline statuses onto the shared status tokens for `StatusDot`. */
function getTakeoffStatusKey(status: string): StatusKey {
  switch (status) {
    case "initiated":
    case "ready_for_tag_selection":
      return "pending";
    case "ready_for_review":
    case "ready_for_results":
      return "review";
    case "done":
    case "completed":
      return "completed";
    case "failed":
      return "error";
    default:
      return "processing";
  }
}

interface TakeoffJobListItem {
  _id: Id<"takeoffPilotJobs">;
  projectId: Id<"projects">;
  name: string;
  status: string;
  userStatus?: TakeoffUserStatus;
  aiTakeoff?: { status: string; completed: number; total: number } | null;
  createdByName: string | null;
  lastWorkedOnAt?: number;
  createdAt: number;
  revisionRootJobId?: Id<"takeoffPilotJobs">;
  revisionSourceJobId?: Id<"takeoffPilotJobs">;
  revisionNumber?: number;
}

function getTakeoffDisplayStatus(job: TakeoffJobListItem): string {
  return job.status === "copying_revision" ? job.status : (job.userStatus ?? DEFAULT_USER_STATUS);
}

export function TakeoffPilotPage() {
  const navigate = useAppNavigate();
  const { projectId, isLoading: isProjectLoading } = useProject();
  const { data: jobs, isPending } = useQuery(convexQuery(api.takeoffPilot.jobs.listAll, {}));
  const removeJob = useMutation(api.takeoffPilot.jobs.remove);
  const updateName = useMutation(api.takeoffPilot.jobs.updateName);
  const updateUserStatus = useMutation(api.takeoffPilot.jobs.updateUserStatus);
  const [deletingJobIds, setDeletingJobIds] = useState<Set<string>>(() => new Set());
  const [openRevisionGroupIds, setOpenRevisionGroupIds] = useState<Set<string>>(() => new Set());
  const [revisionDialogJob, setRevisionDialogJob] = useState<TakeoffJobListItem | null>(null);
  useOnEditAccessLost(() => setRevisionDialogJob(null));
  const visibleJobs = useMemo(
    () => (jobs ?? []).filter((job) => !projectId || job.projectId === projectId),
    [jobs, projectId]
  );
  const revisionGroups = useMemo(
    () => groupTakeoffJobsByRevision(visibleJobs as TakeoffJobListItem[]),
    [visibleJobs]
  );
  // Each group shows its latest revision, followed by older revisions while its history is open.
  const rows = useMemo<TakeoffListRow[]>(
    () =>
      revisionGroups.flatMap((group) => {
        const isHistoryOpen = openRevisionGroupIds.has(group.rootId);
        const primary: TakeoffListRow = {
          job: group.latestJob,
          rootId: group.rootId,
          revisionLevel: 0,
          showRevisionBadge:
            group.historyJobs.length > 0 || getTakeoffRevisionNumber(group.latestJob) > 1,
          revisionHistoryCount: group.historyJobs.length,
          isRevisionHistoryOpen: isHistoryOpen,
        };
        if (!isHistoryOpen) return [primary];
        return [
          primary,
          ...group.historyJobs.map(
            (job): TakeoffListRow => ({
              job,
              rootId: group.rootId,
              revisionLevel: 1,
              showRevisionBadge: true,
              revisionHistoryCount: 0,
              isRevisionHistoryOpen: false,
            })
          ),
        ];
      }),
    [revisionGroups, openRevisionGroupIds]
  );

  const handleToggleRevisionHistory = (rootId: string) => {
    setOpenRevisionGroupIds((current) => {
      const next = new Set(current);
      if (next.has(rootId)) {
        next.delete(rootId);
      } else {
        next.add(rootId);
      }
      return next;
    });
  };

  const handleStartRevPilotReview = (job: TakeoffJobListItem) => {
    navigate({
      to: "/revPilot/new",
      search: { sourceTakeoffJobId: job._id },
      title: "New RevPilot Review",
    });
  };

  const handleNewTakeoff = () => {
    navigate({ to: "/takeoffPilot/new", title: "New TakeoffPilot Job" });
  };

  const handleDeleteJob = async (jobId: Id<"takeoffPilotJobs">) => {
    setDeletingJobIds((current) => {
      const next = new Set(current);
      next.add(String(jobId));
      return next;
    });

    try {
      await removeJob({ jobId });
      toast.success("Job deleted");
    } catch (error) {
      notifyError(error, "Failed to delete job");
      console.error(error);
    } finally {
      setDeletingJobIds((current) => {
        const next = new Set(current);
        next.delete(String(jobId));
        return next;
      });
    }
  };

  const handleUpdateName = async (jobId: Id<"takeoffPilotJobs">, name: string) => {
    try {
      await updateName({ jobId, name });
      toast.success("Name updated");
    } catch (error) {
      notifyError(error, "Failed to update name");
      console.error(error);
      throw error;
    }
  };

  const handleUpdateStatus = async (
    jobId: Id<"takeoffPilotJobs">,
    userStatus: TakeoffUserStatus
  ) => {
    try {
      await updateUserStatus({
        jobId,
        userStatus,
      });
      toast.success("Status updated");
    } catch (error) {
      notifyError(error, "Failed to update status");
      console.error(error);
    }
  };

  const columns: JobListColumn<TakeoffListRow>[] = [
    {
      id: "status",
      header: "Status",
      cell: ({ job }) => <div className="space-y-1"><TakeoffStatusCell job={job} onUpdateStatus={handleUpdateStatus} />
        {job.aiTakeoff && <p className="text-xs text-muted-foreground">{({ setup_required: "AI setup required", preparing: "Preparing AI takeoff", running: "AI takeoff running", ready_for_review: "AI ready to review", needs_attention: "AI takeoff needs attention" } as Record<string, string>)[job.aiTakeoff.status]}</p>}
      </div>,
    },
    {
      id: "created-by",
      header: "Created By",
      variant: "muted",
      cell: ({ job }) => job.createdByName ?? "—",
    },
    jobListDateColumn("Created", ({ job }) => job.createdAt),
    jobListDateColumn("Last Updated", ({ job }) => job.lastWorkedOnAt ?? job.createdAt),
  ];

  return (
    <JobListPage
      title="TakeoffPilot"
      newAction={{ label: "New TakeoffPilot Job", onClick: handleNewTakeoff }}
      emptyState={{
        icon: FileText,
        title: "No TakeoffPilot jobs yet",
        description:
          "Create a TakeoffPilot job from project documents or uploaded plan PDFs to start extracting takeoff quantities.",
      }}
      isLoading={isProjectLoading || isPending}
      rows={rows}
      getRowKey={({ job }) => job._id}
      getRowName={({ job }) => job.name}
      getRowLink={({ job }) => ({
        to: "/takeoffPilot/$jobId",
        params: { jobId: job._id },
        title: job.name,
      })}
      getRowClassName={(row) => (row.revisionLevel > 0 ? "bg-muted/15" : undefined)}
      renderViewExtras={(row) =>
        row.revisionHistoryCount > 0 ? (
          <IconButton
            label={`${row.isRevisionHistoryOpen ? "Hide" : "Show"} revision history for ${row.job.name}`}
            icon={row.isRevisionHistoryOpen ? ChevronDown : ChevronRight}
            tooltip={false}
            className="text-muted-foreground hover:text-foreground"
            aria-expanded={row.isRevisionHistoryOpen}
            onClick={() => handleToggleRevisionHistory(row.rootId)}
          />
        ) : null
      }
      renderName={(row) => (
        <InlineEditNameCell
          value={row.job.name}
          onSave={(name) => handleUpdateName(row.job._id, name)}
          className={cn(row.revisionLevel > 0 && "ps-7")}
          prefix={
            row.showRevisionBadge ? (
              <RevisionBadge number={getTakeoffRevisionNumber(row.job)} />
            ) : null
          }
        />
      )}
      columns={columns}
      renderActions={({ job }) => (
        <TakeoffJobActions
          job={job}
          isDeleting={deletingJobIds.has(String(job._id))}
          onCreateRevision={setRevisionDialogJob}
          onStartRevPilotReview={handleStartRevPilotReview}
          onDelete={handleDeleteJob}
        />
      )}
    >
      <CreateTakeoffRevisionDialog
        jobId={revisionDialogJob?._id ?? null}
        jobName={revisionDialogJob?.name}
        open={revisionDialogJob !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevisionDialogJob(null);
          }
        }}
        onCreated={(jobId) =>
          navigate({
            to: "/takeoffPilot/$jobId",
            params: { jobId },
            title: "Takeoff Revision",
          })
        }
      />
    </JobListPage>
  );
}

interface TakeoffListRow {
  job: TakeoffJobListItem;
  /** Revision group, for toggling its history. */
  rootId: string;
  /** 0 for a group's latest revision, 1 for an older revision shown under it. */
  revisionLevel: number;
  showRevisionBadge: boolean;
  revisionHistoryCount: number;
  isRevisionHistoryOpen: boolean;
}

function TakeoffStatusCell({
  job,
  onUpdateStatus,
}: {
  job: TakeoffJobListItem;
  onUpdateStatus: (jobId: Id<"takeoffPilotJobs">, userStatus: TakeoffUserStatus) => Promise<void>;
}) {
  const canEdit = useCanEdit();
  const displayStatus = getTakeoffDisplayStatus(job);
  const statusLabel = getTakeoffStatusLabel(displayStatus);
  const label = (
    <span className="flex min-w-0 items-center gap-2">
      <StatusDot status={getTakeoffStatusKey(displayStatus)} size="sm" />
      <span className="truncate">{statusLabel}</span>
    </span>
  );

  if (!canEdit) {
    return <span className="text-xs font-medium">{label}</span>;
  }

  return (
    <Select
      value={job.userStatus ?? DEFAULT_USER_STATUS}
      onValueChange={(value) => {
        void onUpdateStatus(job._id, value as TakeoffUserStatus);
      }}
    >
      <SelectTrigger
        size="sm"
        aria-label={`Status for ${job.name}`}
        className="w-[190px] text-xs font-medium"
      >
        {label}
      </SelectTrigger>
      <SelectContent align="start" collisionPadding={8} position="popper" sideOffset={4}>
        {USER_STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface TakeoffJobActionsProps {
  job: TakeoffJobListItem;
  isDeleting: boolean;
  onCreateRevision: (job: TakeoffJobListItem) => void;
  onStartRevPilotReview: (job: TakeoffJobListItem) => void;
  onDelete: (jobId: Id<"takeoffPilotJobs">) => Promise<void>;
}

function TakeoffJobActions({
  job,
  isDeleting,
  onCreateRevision,
  onStartRevPilotReview,
  onDelete,
}: TakeoffJobActionsProps) {
  const canEdit = useCanEdit();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  useOnEditAccessLost(() => setIsDeleteDialogOpen(false));

  if (!canEdit) return null;

  return (
    <>
      <RowActionsMenu label={`Actions for ${job.name}`} disabled={isDeleting}>
        <RowAction icon={GitBranchPlus} onSelect={() => onCreateRevision(job)}>
          Create revision
        </RowAction>
        <RowAction icon={FileText} onSelect={() => onStartRevPilotReview(job)}>
          Start RevPilot review
        </RowAction>
        <RowAction
          icon={Trash2}
          destructive
          disabled={isDeleting}
          onSelect={() => setIsDeleteDialogOpen(true)}
        >
          Delete
        </RowAction>
      </RowActionsMenu>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete TakeoffPilot Job"
        description={`Are you sure you want to delete "${job.name}"? This action cannot be undone.`}
        onConfirm={() => onDelete(job._id)}
        disabled={isDeleting}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </>
  );
}
